import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { X, Zap, List, Archive, LayoutDashboard } from 'lucide-react';
import { cn } from '../lib/utils';
import { getOrgType } from '../authUtils';

const NAV_SECTIONS_BY_ORG_TYPE = {
  INTERNAL: [
    {
      title: 'CARRIER ENGAGEMENT DASHBOARD',
      items: [
        {
          to: '/carrier-engagement-dashboard',
          icon: LayoutDashboard,
          label: 'Carrier Engagement Dashboard',
        },
      ],
    },
    {
      title: 'REFERRAL PORTAL',
      items: [
        { to: '/referrals', icon: List, label: 'Referrals' },
        { to: '/closed-referrals', icon: Archive, label: 'Closed Referrals' },
      ],
    },
    {
      title: 'INTEGRATIONS',
      items: [{ to: '/integrations', icon: Zap, label: 'Integrations' }],
    },
  ],
  CARRIER: [
    {
      title: 'REFERRAL PORTAL',
      items: [
        { to: '/referrals', icon: List, label: 'Referrals' },
        { to: '/closed-referrals', icon: Archive, label: 'Closed Referrals' },
      ],
    },
  ],
  TPA: [
    {
      title: 'REFERRAL PORTAL',
      items: [
        { to: '/referrals', icon: List, label: 'Referrals' },
        { to: '/closed-referrals', icon: Archive, label: 'Closed Referrals' },
      ],
    },
  ],
  CLIENT: [
    {
      title: 'REFERRAL PORTAL',
      items: [
        { to: '/referrals', icon: List, label: 'Referrals' },
        { to: '/closed-referrals', icon: Archive, label: 'Closed Referrals' },
      ],
    },
  ],
};

const BurgerNavItem = ({ to, icon: Icon, label, onClick }) => (
  <NavLink
    to={to}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        'flex items-center gap-3 pl-7 pr-4 py-2 rounded-md text-sm font-medium transition-colors',
        'hover:bg-slate-100 hover:text-slate-900',
        isActive ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-700'
      )
    }
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </NavLink>
);

const BurgerNavSection = ({ title, items, onNavItemClick }) => (
  <div className="mb-4">
    <h3 className="mb-1 px-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
      {title}
    </h3>
    <nav className="space-y-1">
      {items.map((item) => (
        <BurgerNavItem
          key={item.to}
          to={item.to}
          icon={item.icon}
          label={item.label}
          onClick={onNavItemClick}
        />
      ))}
    </nav>
  </div>
);

function BurgerMenu({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const orgType = getOrgType();
  const sections = NAV_SECTIONS_BY_ORG_TYPE[orgType] ?? [];

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="burger-menu-overlay"
        className={cn(
          'fixed inset-0 z-40 bg-black/20 transition-opacity duration-300',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        data-testid="burger-menu-panel"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col w-64',
          'bg-white overflow-hidden shadow-2xl',
          'transition-transform ease-in-out duration-300',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        aria-hidden={!open}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-sm p-1 text-slate-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
          aria-label="Close menu"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Nav sections */}
        <div className="flex-1 overflow-y-auto py-4 pt-12">
          {sections.map((section) => (
            <BurgerNavSection
              key={section.title}
              title={section.title}
              items={section.items}
              onNavItemClick={onClose}
            />
          ))}
        </div>
      </div>
    </>
  );
}

export { BurgerMenu };
